import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { allExercises, putExercise } from '../../db/repo';
import { MUSCLE_GROUPS, MUSCLE_LABEL } from '../../domain/types';
import type { Exercise, MuscleGroup } from '../../domain/types';
import { buildCustomExercise, shouldOfferCustom } from '../../domain/customExercise';
import { Sheet } from '../../ui/primitives';

// Pick an exercise (for add or substitute). Filterable by name; grouped hint by
// equipment area so a busy-rack swap is quick.
export function ExercisePicker({
  title, onPick, onClose, excludeId,
}: {
  title: string;
  onPick: (ex: Exercise) => void;
  onClose: () => void;
  excludeId?: string;
}) {
  const exercises = useLiveQuery(() => allExercises(), []);
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const list = (exercises ?? []).filter((e) => e.id !== excludeId);
    if (!q.trim()) return list;
    const needle = q.toLowerCase();
    return list.filter(
      (e) => e.name.toLowerCase().includes(needle) || MUSCLE_LABEL[e.muscleGroup].toLowerCase().includes(needle),
    );
  }, [exercises, q, excludeId]);

  // Freestyle: the typed name isn't in the catalog (gym lacks the machine).
  const [muscle, setMuscle] = useState<MuscleGroup | ''>('');
  const [perHand, setPerHand] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const offerCustom = shouldOfferCustom(q, (exercises ?? []).map((e) => e.name));

  async function addCustom() {
    if (!muscle) { setError('Pick a muscle group first (it counts toward weekly volume).'); return; }
    const all = await allExercises(true);
    const ex = buildCustomExercise(q, muscle, perHand, new Set(all.map((e) => e.id)));
    await putExercise(ex);
    onPick(ex);
  }

  return (
    <Sheet title={title} onClose={onClose}>
      <input
        className="input"
        placeholder="Search, or type a new exercise name"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
        style={{ marginBottom: 12 }}
      />
      {filtered.map((ex) => (
        <button key={ex.id} className="list-tap" onClick={() => onPick(ex)}>
          <div>
            <strong>{ex.name}</strong>
            <div className="faint" style={{ fontSize: 12 }}>
              {MUSCLE_LABEL[ex.muscleGroup]} - {ex.equipmentArea}
            </div>
          </div>
          <span className="chip">{ex.structure === 'amrap' ? 'AMRAP' : ex.structure === 'ramp_top_backoff' ? 'Top set' : `${ex.defaultRepRange.bottom}-${ex.defaultRepRange.top}`}</span>
        </button>
      ))}
      {filtered.length === 0 && !offerCustom && <div className="empty">No match.</div>}
      {offerCustom && (
        <div className="card tight" style={{ marginTop: 8 }}>
          <strong>Freestyle: "{q.trim()}"</strong>
          <div className="faint" style={{ fontSize: 12, margin: '2px 0 10px' }}>
            Not in the catalog? Add it and log as usual. It stays in search for next time.
          </div>
          <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
            <select
              className="input"
              aria-label="muscle group"
              value={muscle}
              onChange={(e) => { setMuscle(e.target.value as MuscleGroup | ''); setError(null); }}
              style={{ flex: 1, minWidth: 140 }}
            >
              <option value="">Muscle group…</option>
              {MUSCLE_GROUPS.map((m) => <option key={m} value={m}>{MUSCLE_LABEL[m]}</option>)}
            </select>
            <label className="row" style={{ gap: 6, fontSize: 14 }}>
              <input type="checkbox" checked={perHand} onChange={(e) => setPerHand(e.target.checked)} />
              Dumbbells (kg/hand)
            </label>
          </div>
          {error && <div style={{ color: 'var(--bad, #d03b3b)', fontSize: 13, marginTop: 6 }}>{error}</div>}
          <button className="btn primary full" style={{ marginTop: 10 }} onClick={addCustom}>
            Add "{q.trim()}"
          </button>
        </div>
      )}
    </Sheet>
  );
}
